import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, resolveComponent as _resolveComponent, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "relative flex flex-col justify-center w-full" }
const _hoisted_2 = { class: "w-full py-5 bg-gray-50/60 dark:bg-gray-900/40 border-y border-gray-100 dark:border-gray-800/60" }
const _hoisted_3 = { class: "text-sm font-medium text-gray-600 dark:text-gray-300 whitespace-nowrap" }
const _hoisted_4 = { class: "sm:mb-0 md:my-0 px-4 md:px-10 lg:px-20" }
const _hoisted_5 = { class: "sm:h-auto flex justify-center items-center" }
const _hoisted_6 = { class: "pt-0 bg-primary-100/90 dark:bg-elevated/80 dark:dark-frosted-bg frosted-bg relative z-20 flex justify-center" }
const _hoisted_7 = { class: "mt-16" }
const _hoisted_8 = { class: "flex flex-col md:flex-row gap-8 w-full mt-8" }
const _hoisted_9 = { class: "flex flex-col md:flex-row gap-8 w-full mt-8" }

type FeatureCardImage = {
  path: string;
  width: number;
  height: number;
};

type FeatureCard = {
  title: string;
  description: string;
  icon?: string;
  color?: string;
  variant?: "soft" | "solid" | "outline" | "subtle" | "ghost" | "naked";
  animation: string;
  delay: number;
  image?: FeatureCardImage;
  class?: string;
  orientation?: "horizontal" | "vertical";
  reverse?: boolean;
};

const lightPreview = `/images/app_preview.png`;
const darkPreview = `/images/app_preview_dark.png`;

interface LinkPreview {
  label: string;
  to: string;
  variant?: "soft" | "solid" | "outline" | "subtle" | "ghost" | "link";
  trailingIcon?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const config = useRuntimeConfig();
const colorMode = useColorMode();
const themeColor = computed(() => (colorMode.value === "dark" ? "#171717" : "#f5f5f5"));

definePageMeta({
  layout: "default",
});

useHead({
  title: "FamilyAxis",
  meta: [
    { name: "description", content: "Family Assessment made easy." },
    { name: "theme-color", content: themeColor.value },
  ],
  bodyAttrs: {
    class: "Home",
  },
  link: [{ rel: "canonical", href: "https://familyaxis.com/" }],
});

defineOgImage("OgHome", {
  title: "FAMILY ASSESSMENT SOFTWARE",
  description: "One platform. Built for family-centred teams.",
  theme: "#f5f5f5",
});

useSeoMeta({
  title: "Specialist Software for Family Assessment Centres | FamilyAxis",
  description:
    "FamilyAxis makes family assessment easy. Log observations, generate court-ready reports, manage safeguarding concerns, track direct work, and centralise operations in one secure platform.",
  keywords:
    "family assessment software, residential family assessment centre, observation logs, safeguarding, direct work tracking, court reports, parent profiles, genogram, home management, UK GDPR compliant",
  author: "FamilyAxis",
  ogTitle: "Specialist Software for Family Assessment Centres | FamilyAxis",
  ogDescription:
    "FamilyAxis makes family assessment easy for residential centres and community work, with observations, reports, safeguarding, and more.",
  ogUrl: "https://familyaxis.com/",
  ogType: "website",
  ogSiteName: "FamilyAxis",
  twitterCard: "summary_large_image",
  twitterTitle: "Specialist Software for Family Assessment Centres | FamilyAxis",
  twitterDescription:
    "All-in-one toolbox for residential family assessment centres. Observations, reports, safeguarding and more.",
  twitterSite: "@familyaxis",
  robots: "index, follow",
});

// Define types for the featureCard items
const featureCards = ref<FeatureCard[]>([
  {
    title: "Home Management System",
    description:
      "Streamline your operations with tools for logging observations, direct work sessions, and family feedback. From safeguarding concerns, health and safety logs to final court-ready reports, everything you need is in one intuitive platform.",
    icon: "i-heroicons-outline:building-storefront",
    animation: "fade-up",
    delay: 50,
    image: {
      path: "/images/Research_Findings.svg",
      width: 362,
      height: 184,
    },
    class: "lg:col-span-2",
    orientation: "horizontal",
    reverse: true,
  },

  {
    title: "Efficient and Customisable",
    description:
      "Search and sort functionality designed to save you time. With a custom template creator and flexible tools, FamilyAxis lets you tailor the platform to suit your organisation and the families you support.",
    icon: "i-clarity-bolt-line",
    variant: "soft",
    animation: "fade-up",
    delay: 50,
  },

  {
    title: "Family Focused",
    description:
      "Our tools for life story work, parent profiles, and an interactive genogram help keep children at the heart of every assessment, reflecting the needs and wishes of each family.",
    icon: "i-mdi-light:diamond",
    color: "primary",
    variant: "soft",
    animation: "fade-up",
    delay: 50,
  },
  {
    title: "Secure and Compliant",
    description:
      "Meet legal standards and court requirements with confidence. All data is securely stored, ensuring confidentiality while maintaining compliance with safeguarding regulations.",
    icon: "i-heroicons-lock-closed",
    color: "primary",
    animation: "fade-up",
    delay: 50,
    image: {
      path: "/images/secu_shield.svg",
      width: 362,
      height: 184,
    },
    class: "lg:col-span-2",
    orientation: "horizontal",
    reverse: true,
  },
]);

const _itemsFAQ = [
  {
    label: "What is FamilyAxis?",
    icon: "i-lucide-circle-help",
    content: `FamilyAxis is a comprehensive software solution specifically designed to support residential
  family assessment centres. It streamlines tasks like logging observations, generating
  reports, managing safeguarding concerns, and handling day-to-day operations.`,
  },
  {
    label: "Is FamilyAxis only for residential family assessment centres?",
    icon: "i-lucide-circle-help",
    content: `While designed for residential centres, FamilyAxis can also support community-based family assessments,
  parenting programs, and services that require structured observations, safeguarding documentation, and direct work tracking.`,
  },
  {
    label: "Does FamilyAxis support different assessment frameworks?",
    icon: "i-lucide-circle-help",
    content: `
  Yes! FamilyAxis is fully customisable, allowing centres to tailor assessments to their specific approach.

  __Customisable Assessment Areas__
  - Staff can adapt and modify assessment areas to align with their preferred methodology.

  __Create Your Own Templates__
  - Managers have the flexibility to design and implement custom templates, ensuring assessments reflect the centre's unique requirements.
  `,
  },
  {
    label: "What features does FamilyAxis offer?",
    icon: "i-lucide-circle-help",
    content: `
  - ✔ Observations – Structured logging with a clear grading system
  - ✔ Report Generation – Automatically create court-ready reports
  - ✔ Direct Work Tracking – Plan and document parenting interventions
  - ✔ Daily Family Feedback & Handovers – Track progress and key moments
  - ✔ Home Management Tools – Cleaning tasks, health & safety checks, and more
  - ✔ Custom Templates – Personalised forms for observations and reports
  - ✔ Parent Profiles – Secure login for parents to view their feedback and progress
  - ✔ Task Reminders & Real-Time Tracking – Automate key tasks and deadlines
  - ✔ Data Visualisation & Insights – Graphs to identify trends and concerns
  - ✔ Genogram Tool – Visually map family relationships, dynamics, and intergenerational patterns`,
  },
  {
    label: "Is FamilyAxis GDPR compliant?",
    icon: "i-lucide-circle-help",
    content: `Yes. FamilyAxis prioritises security and data protection, with encrypted access, role-based permissions,
  and secure data storage to ensure compliance with UK and EU regulations.`,
  },
  {
    label: "What devices and browsers are supported?",
    icon: "i-lucide-circle-help",
    content: `FamilyAxis works on all major browsers (Chrome, Safari, Firefox, Edge) and devices (iOS, Android, Windows, macOS).`,
  },
];

const linksPreview = ref<LinkPreview[]>([
  {
    label: "Get Started",
    to: "/signup",
  },
  {
    label: "Contact Us",
    to: "/contact-sale",
    variant: "outline",
    trailingIcon: "i-lucide-arrow-right",
  },
]);

const marqueeItems = [
  { icon: "i-lucide-credit-card", label: "No credit card needed" },
  { icon: "i-lucide-rocket", label: "Start for free" },
  { icon: "i-lucide-shield-check", label: "Secure cloud hosting" },
  { icon: "i-lucide-sparkles", label: "AI Integration" },
  { icon: "i-lucide-users", label: "Staff Management ToolSet" },
  { icon: "i-lucide-clipboard-list", label: "Observation & Direct Work Tools" },
  { icon: "i-lucide-heart", label: "Parent & Child In-Depth Profiles" },
];

return (_ctx: any,_cache: any) => {
  const _component_HeroShowcaseCard = _resolveComponent("HeroShowcaseCard")!
  const _component_UPage = _resolveComponent("UPage")!
  const _component_UContainer = _resolveComponent("UContainer")!
  const _component_UIcon = _resolveComponent("UIcon")!
  const _component_UMarquee = _resolveComponent("UMarquee")!
  const _component_VideoShowcaseCard = _resolveComponent("VideoShowcaseCard")!
  const _component_UColorModeImage = _resolveComponent("UColorModeImage")!
  const _component_UPageCard = _resolveComponent("UPageCard")!
  const _component_UPageGrid = _resolveComponent("UPageGrid")!
  const _component_LazyWorkSteps = _resolveComponent("LazyWorkSteps")!
  const _component_LazyFeaturesBento = _resolveComponent("LazyFeaturesBento")!
  const _component_UPageHero = _resolveComponent("UPageHero")!
  const _component_ShowCaseCard = _resolveComponent("ShowCaseCard")!
  const _component_Motion = _resolveComponent("Motion")!
  const _component_LazyStartSignup = _resolveComponent("LazyStartSignup")!
  const _component_UPageBody = _resolveComponent("UPageBody")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[0] || (_cache[0] = _createStaticVNode("<svg width=\"0\" height=\"0\" class=\"absolute\" data-v-38ef6196><defs data-v-38ef6196><clipPath id=\"curved-top-clip-sm\" clipPathUnits=\"objectBoundingBox\" data-v-38ef6196><path d=\"M0,0.03 C0.3,0, 0.7,0.06, 1,0.03 V1 H0 Z\" data-v-38ef6196></path></clipPath><clipPath id=\"curved-top-clip-md\" clipPathUnits=\"objectBoundingBox\" data-v-38ef6196><path d=\"M0,0.05 C0.3,0, 0.7,0.1, 1,0.05 V1 H0 Z\" data-v-38ef6196></path></clipPath><clipPath id=\"curved-top-clip\" clipPathUnits=\"objectBoundingBox\" data-v-38ef6196><path d=\"M0,0.08 C0.3,0, 0.7,0.16, 1,0.08 V1 H0 Z\" data-v-38ef6196></path></clipPath></defs></svg>", 1)),
    _createVNode(_component_UContainer, {
      id: "hometop",
      class: "z-20 py-0 w-[90vw] max-w-[95vw] flex justify-center",
      ui: {
        container: { base: 'w-full max-w-[95vw] mx-4 px-2 sm:px-2 lg:px-2 py-0' },
      }
    }, {
      default: _withCtx(() => [
        _createVNode(_component_UPage, { class: "flex flex-col justify-center py-0" }, {
          default: _withCtx(() => [
            (!_unref(config).public.prelaunch)
              ? (_openBlock(), _createBlock(_component_HeroShowcaseCard, {
                  key: 0,
                  title: "The Family Assessment Solution",
                  links: _unref(linksPreview),
                  "light-image": lightPreview,
                  "dark-image": darkPreview,
                  "alt-text": "FamilyAxis Dashboard"
                }, null, 8, ["links"]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        })
      ]),
      _: 1
    }),
    _createElementVNode("div", _hoisted_2, [
      _createVNode(_component_UMarquee, {
        "pause-on-hover": "",
        overlay: true,
        class: "[--duration:35s]",
        ui: { root: '[--gap:--spacing(8)]' }
      }, {
        default: _withCtx(() => [
          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(marqueeItems, (item, index) => {
            return _createElementVNode("div", {
              key: index,
              class: "flex items-center gap-2.5 shrink-0"
            }, [
              _createVNode(_component_UIcon, {
                name: item.icon,
                class: "size-4 text-primary"
              }, null, 8, ["name"]),
              _createElementVNode("span", _hoisted_3, _toDisplayString(item.label), 1)
            ])
          }), 64))
        ]),
        _: 1
      })
    ]),
    _createVNode(_component_VideoShowcaseCard),
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("div", _hoisted_5, [
        _createVNode(_component_UPageGrid, {
          class: "w-10/12 pt-10",
          ui: {
            pageBody: {
              base: 'mt-8 pb-10 space-y-10',
            },
          }
        }, {
          default: _withCtx(() => [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(featureCards), (card, index) => {
              return (_openBlock(), _createBlock(_component_UPageCard, {
                key: index,
                title: card.title,
                description: card.description,
                icon: card.icon,
                color: card.color,
                variant: card.variant,
                class: _normalizeClass(card.class),
                orientation: card.orientation,
                reverse: card.reverse
              }, {
                default: _withCtx(() => [
                  (card.image)
                    ? (_openBlock(), _createBlock(_component_UColorModeImage, {
                        key: 0,
                        light: `${card.image.path}`,
                        dark: `${card.image.path}`,
                        width: card.image.width,
                        height: card.image.height,
                        style: _normalizeStyle(`object-fit: contain; width:${card.image.width}px;height:${card.image.height}px;`),
                        alt: card.title,
                        loading: "lazy",
                        class: "w-full"
                      }, null, 8, ["light", "dark", "width", "height", "style", "alt"]))
                    : _createCommentVNode("", true)
                ]),
                _: 2
              }, 1032, ["title", "description", "icon", "color", "variant", "class", "orientation", "reverse"]))
            }), 128))
          ]),
          _: 1
        })
      ])
    ]),
    _createVNode(_component_LazyWorkSteps),
    _createVNode(_component_LazyFeaturesBento),
    _createElementVNode("div", _hoisted_6, [
      _createVNode(_component_UContainer, null, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_7, [
            _createVNode(_component_UPageHero, {
              title: "Follow the Journey of Each Family",
              description: "Support parents and children with dedicated tools that keep families at the heart of your work.",
              ui: {
              container: 'py-8 sm:py-6 lg:py-6',
              title: 'text-4xl sm:text-4xl text-pretty tracking-tight font-bold',
              // description: 'text-lg sm:text-xl/8 text-muted dark:text-neutral-300 max-w-180',
            }
            }),
            _createVNode(_component_Motion, {
              initial: { opacity: 0 },
              animate: { opacity: 1 },
              transition: { duration: 0.6 },
              class: "flex flex-col md:flex-row gap-8 w-full mt-8"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_ShowCaseCard, {
                  title: "Resident Profiles",
                  description: "Every parent and child has a dedicated space to track their journey and needs",
                  icon: "i-heroicons-user-circle",
                  image: "/howitworks/activate_resident.webp",
                  "image-position": "bottom",
                  link: "/more/resident-profiles",
                  class: "w-full md:w-4/7 h-110"
                }),
                _createVNode(_component_ShowCaseCard, {
                  title: "Family Engagement",
                  description: "Tools for life story work and parent profiles to ensure the family's voice is always heard.",
                  icon: "i-heroicons-heart",
                  image: "/more/story.webp",
                  "image-position": "cover",
                  link: "/more/family-engagement",
                  reverse: true,
                  class: "w-full md:w-3/7 h-110"
                })
              ]),
              _: 1
            })
          ]),
          _createVNode(_component_Motion, {
            initial: { opacity: 0 },
            animate: { opacity: 1 },
            transition: { duration: 0.6 },
            class: "mt-16"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_UPageHero, {
                title: "Track Progress with Precision",
                description: "Monitor progress, log observations, and manage direct work to ensure assessments are thorough and up-to-date.",
                ui: {
              container: 'py-8 sm:py-6 lg:py-6',
              title: 'text-4xl sm:text-4xl text-pretty tracking-tight font-bold',
            }
              }),
              _createElementVNode("div", _hoisted_8, [
                _createVNode(_component_ShowCaseCard, {
                  title: "Direct Work",
                  description: "Customise and track direct work sessions.",
                  icon: "i-heroicons-academic-cap",
                  image: "/images/dw_track.svg",
                  "image-position": "bottom",
                  link: "/more/direct-work",
                  class: "w-full md:w-3/7 h-110"
                }),
                _createVNode(_component_ShowCaseCard, {
                  title: "Progress & Observations",
                  description: "Log observations and track progress against assessment goals.",
                  icon: "i-heroicons-chart-bar-square",
                  image: "/images/obs_track.svg",
                  "image-position": "bottom",
                  link: "/more/progress-observations",
                  class: "w-full md:w-4/7 h-110"
                })
              ])
            ]),
            _: 1
          }),
          _createVNode(_component_Motion, {
            initial: { opacity: 0 },
            animate: { opacity: 1 },
            transition: { duration: 0.6 },
            class: "mt-16"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_UPageHero, {
                title: "Centralise Your Operations",
                description: "A unified dashboard for staff and managers to streamline workflows, access key information, and manage the centre efficiently.",
                ui: {
              container: 'py-8 sm:py-6 lg:py-6',
              title: 'text-4xl sm:text-4xl text-pretty tracking-tight font-bold',
            }
              }),
              _createElementVNode("div", _hoisted_9, [
                _createVNode(_component_ShowCaseCard, {
                  title: "Centralised Dashboard",
                  description: "Manage all your operations from a single dashboard.",
                  icon: "i-heroicons-computer-desktop",
                  image: "/images/dash_preview.webp",
                  "image-position": "bottom",
                  link: "/more/centralised-dashboard",
                  class: "w-full md:w-4/7 h-110"
                }),
                _createVNode(_component_ShowCaseCard, {
                  title: "Team Collaboration",
                  description: "Assign tasks, share updates, and work together seamlessly.",
                  icon: "i-heroicons-user-group",
                  image: "/images/tasks_preview.svg",
                  "image-position": "right",
                  link: "/more/team-collaboration",
                  class: "w-full md:w-3/7 h-110"
                })
              ])
            ]),
            _: 1
          }),
          _createVNode(_component_LazyStartSignup)
        ]),
        _: 1
      })
    ]),
    _createVNode(_component_UContainer, { class: "z-20" }, {
      default: _withCtx(() => [
        _createVNode(_component_UPage, null, {
          default: _withCtx(() => [
            _createVNode(_component_UPageBody, { class: "py-10" })
          ]),
          _: 1
        })
      ]),
      _: 1
    })
  ]))
}
}

})